import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

const BrowserCall = React.lazy(() => import("./BrowserCall").then(m => ({ default: m.BrowserCall })));
const CallRecordings = React.lazy(() => import("./CallRecordings").then(m => ({ default: m.CallRecordings })));
const PhoneNumbersList = React.lazy(() => import("./PhoneNumbersList").then(m => ({ default: m.PhoneNumbersList })));
const PhoneNumberAssignmentAdmin = React.lazy(() => import("./PhoneNumberAssignmentAdmin").then(m => ({ default: m.PhoneNumberAssignmentAdmin })));
const PhoneLines = React.lazy(() => import("./PhoneLines").then(m => ({ default: m.PhoneLines })));
const CrazytelSetup = React.lazy(() => import("./CrazytelSetup").then(m => ({ default: m.CrazytelSetup })));

const phoneSystemModuleConfig: ModuleConfig = {
  id: "phone-system",
  name: "Voice",
  description: "Manage phone numbers, call recordings, and transcriptions",
  icon: "📞",
  version: "1.3.0",
  enabled: true,
  category: "communication",
  order: 10,
  routes: [
    { path: "/phone-system", component: BrowserCall, permissionLevel: "view", useLayout: true },
    { path: "/phone-system/browser-call", component: BrowserCall, permissionLevel: "view", useLayout: true },
    { path: "/phone-system/recordings", component: CallRecordings, permissionLevel: "view", useLayout: true },
    { path: "/phone-system/numbers", component: PhoneNumbersList, permissionLevel: "edit", useLayout: true },
    { path: "/phone-system/assignments", component: PhoneNumberAssignmentAdmin, permissionLevel: "edit", useLayout: true },
    { path: "/phone-system/lines", component: PhoneLines, permissionLevel: "edit", useLayout: true },
    { path: "/phone-system/crazytel", component: CrazytelSetup, permissionLevel: "edit", useLayout: true },
  ],
  // A–Z by label (rule 45). The browser phone lives on the Address book page, so it has no second entry.
  navigation: [
    { path: "/phone-system", label: "Address book", icon: "📞", permissionLevel: "view" },
    { path: "/phone-system/recordings", label: "Call recordings", icon: "🎙️", permissionLevel: "view" },
    { path: "/phone-system/crazytel", label: "Crazytel setup", icon: "🔌", permissionLevel: "edit" },
    { path: "/phone-system/lines", label: "Desk phones", icon: "☎️", permissionLevel: "edit" },
    { path: "/phone-system/assignments", label: "Number allocation", icon: "🔗", permissionLevel: "edit" },
    { path: "/phone-system/numbers", label: "Phone numbers", icon: "📱", permissionLevel: "edit" },
  ],
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },
  quickActions: [
    {
      label: "Manage numbers",
      path: "/phone-system/numbers",
      icon: "➕",
      description: "Purchase a new phone number",
    },
    {
      label: "View recordings",
      path: "/phone-system/recordings",
      icon: "🎙️",
      description: "View call recordings",
    },
  ],

  // 📝 CHANGELOG
  changelog: [
        {
            version: "1.3.0",
            releaseDate: "2026-10-22",
            title: "POLISH v1 — one look across every Voice page",
            features: [
                "Every page opens the same way and links to every other Voice page, A–Z; administration pages are hidden from people who can't open them",
                "Bigger, clearer buttons (at least 44px, words on every one, full width on a phone) and text no smaller than 14px",
                "Phone numbers: settings and transfers are changed on the number's own card; buying, releasing and transferring ask first in a proper dialog",
                "Loading, error and empty are now three different screens; errors say what happened in plain words and offer Try again",
                "Lists you pick from (numbers, desk phones, people, contacts) are A–Z",
                "Statuses read in plain words (In use, Retired, Incoming, Transcript ready…) from one shared list",
            ],
        },
        {
            version: "1.2.0",
            releaseDate: "2026-10-22",
            title: "Crazytel desk phones",
            features: [
                "Desk phones page: one line per Yealink phone, named like its Crazytel SIP trunk",
                "Crazytel setup page: API address, key and webhook secret (write-only, saved after a two-step check), webhook address to copy, check-now button",
                "Recordings: desk-phone calls arrive by webhook or a 5-minute check, are stored on the site's own storage and transcribed",
                "Recordings show which phone took the call and the resident or family contact it was with, linked only for people allowed to open residents",
                "Opening recordings now needs two-step verification (calls carry health information)",
                "Times shown in Brisbane time",
            ],
            notes: "Synced from the TMS Voice module (1.1.0) plus the Crazytel provider. The Crazytel API is not yet confirmed; see the backend phone-system AGENTS.md.",
        },
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready digital phone system",
                "✅ Browser-based calling with Twilio integration",
                "✅ Call recording management with transcriptions",
                "✅ Phone number purchasing and assignment",
                "✅ Comprehensive call dashboard and analytics",
                "✅ Multi-user phone number assignments",
                "✅ Complete call history and recordings"
            ],
            notes: "Official stable release with full digital phone system capabilities."
        },
        {
            version: "0.8.0",
            releaseDate: "2025-09-20",
            title: "Theme System Migration",
            features: [
                "✨ Full light/dark mode support (52 insertions)",
                "✨ Enhanced CallRecordings with theme awareness (40 lines)",
                "✨ Updated PhoneNumbersList color modes (30 lines)",
                "✨ Improved PhoneSystemDashboard themes (28 lines)",
                "✨ Consistent visual design system"
            ],
            notes: "Complete theme system implementation for better user experience."
        },
        {
            version: "0.7.0",
            releaseDate: "2025-09-13",
            title: "GraphQL Code Generation",
            features: [
                "✨ Added GraphQL codegen support",
                "✨ Fixed nullable field handling (17 deletions, 5 insertions)",
                "✨ Enhanced TypeScript type safety",
                "✨ Improved API integration for phone system"
            ],
            notes: "GraphQL integration improvements for better type safety."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-09-02",
            title: "Browser Call Enhancements",
            features: [
                "✨ Enhanced BrowserCall component (175 lines)",
                "✨ Added advanced call controls",
                "✨ Improved call quality monitoring",
                "✨ Better error handling and recovery"
            ],
            notes: "Advanced browser calling features and reliability improvements."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-09-01",
            title: "Dashboard Refactor & Call Management",
            features: [
                "🔨 Major PhoneSystemDashboard refactor (1,089 lines)",
                "✨ Enhanced call management (423 insertions, 666 deletions)",
                "✨ Improved user experience and navigation",
                "✨ Better performance and code organization"
            ],
            notes: "Comprehensive dashboard refactoring for better maintainability."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-08-31",
            title: "Call Recording Features",
            features: [
                "✨ Added sync functionality for call recordings (105 lines)",
                "✨ Implemented transcription feature (50 lines)",
                "✨ Enhanced UI for recordings (88 lines)",
                "✨ Improved PhoneNumberAssignmentAdmin (321 insertions)",
                "✨ Better visual consistency and usability"
            ],
            notes: "Advanced call recording capabilities with transcription support."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-08-30",
            title: "Number Assignment Management",
            features: [
                "✨ Created PhoneNumberAssignmentAdmin (466 lines)",
                "✨ Enhanced BrowserCall component (339 insertions)",
                "✨ Improved PhoneSystemDashboard integration (12 lines)",
                "🔨 Removed MakeOutboundCall component (355 deletions)",
                "✨ Updated dialpad to show by default"
            ],
            notes: "Phone number assignment features for multi-user management."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-08-27",
            title: "Branding & UI Consistency",
            features: [
                "✨ Enhanced BrowserCall branding (127 lines)",
                "✨ Improved CallRecordings UI (281 insertions)",
                "✨ Better MakeOutboundCall interface (130 lines)",
                "✨ Enhanced PhoneNumbersList presentation (197 lines)",
                "✨ Updated PhoneSystemDashboard design (235 lines)",
                "✨ Improved responsive design (228 insertions)",
                "✨ Enhanced brand configuration integration"
            ],
            notes: "Comprehensive UI improvements and brand consistency updates."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-08-23",
            title: "Initial Phone System Implementation",
            features: [
                "✅ Created CallRecordings component (556 lines)",
                "✅ Created PhoneNumbersList component (679 lines)",
                "✅ Created PhoneSystemDashboard (752 lines)",
                "✅ Created MakeOutboundCall component (272 lines)",
                "✅ Implemented BrowserCall with Twilio (341 lines)",
                "✅ WebSocket and region monitoring",
                "✅ Initial GraphQL integration",
                "✅ Module configuration and routing",
                "✅ Twilio API key integration"
            ],
            notes: "Initial implementation of the digital phone system with 1,284 lines of code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default phoneSystemModuleConfig;
